<template>
    <main class="ub-v2" role="main">
        <portal to="ariane">
            <div class="tbv2-page-title">
                <router-link to="/company/users" class="tbv2-crumb">{{ $t('menu.companies.back_office') }}</router-link>
                <span class="tbv2-sep">/</span>
                <span class="tbv2-current">{{ title }}</span>
            </div>
        </portal>

        <header class="ub-hero">
            <router-link to="/company/users" class="ub-back">
                <i class="fal fa-arrow-left"></i>
                <span>{{ $t('common.back') || 'Retour' }}</span>
            </router-link>
            <h1 class="ub-hero-title">{{ title }}</h1>
            <p class="ub-hero-sub">{{ subtitle }}</p>
        </header>

        <section class="ub-card">
            <header class="ub-card-header">
                <div class="ub-card-icon">
                    <i class="fal fa-user"></i>
                </div>
                <div>
                    <h2 class="ub-card-title">{{ $t('companies.name') }}</h2>
                    <p class="ub-card-desc">{{ subtitle }}</p>
                </div>
            </header>

            <form class="ub-form" @submit.prevent="doCreate">
                <div class="ub-grid">
                    <div class="ub-field ub-field--full">
                        <label class="ub-label" for="user-name">{{ $t('companies.name') }}</label>
                        <input
                            id="user-name"
                            v-model="user.name"
                            type="text"
                            class="ub-input"
                        />
                        <p v-if="error.name" class="ub-error">{{ $t(`error.${error.name}`) }}</p>
                    </div>

                    <div class="ub-field ub-field--full">
                        <label class="ub-label" for="user-email">{{ $t('companies.email') }}</label>
                        <input
                            id="user-email"
                            v-model="user.email"
                            type="email"
                            class="ub-input"
                        />
                        <p v-if="error.email" class="ub-error">{{ $t('error.validation.mail_valid') }}</p>
                    </div>

                    <div class="ub-field">
                        <label class="ub-label" for="user-job-title">{{ $t('companies.function') }}</label>
                        <input
                            id="user-job-title"
                            v-model="user.job_title"
                            type="text"
                            class="ub-input"
                        />
                        <p v-if="error.job_title" class="ub-error">{{ $t(`error.${error.job_title}`) }}</p>
                    </div>

                    <div class="ub-field">
                        <label class="ub-label" for="user-phone">{{ $t('companies.phone') }}</label>
                        <input
                            id="user-phone"
                            v-model="user.phone"
                            type="text"
                            class="ub-input"
                            placeholder="+33612345678"
                        />
                        <p v-if="error.phone" class="ub-error">{{ $t(`error.${error.phone}`) }}</p>
                    </div>

                    <div class="ub-field">
                        <label class="ub-label" for="user-locale">{{ $t('companies.language') }}</label>
                        <div class="ub-select-wrapper">
                            <select id="user-locale" v-model="user.locale" class="ub-input ub-select">
                                <option value="fr">Français</option>
                                <option value="en">English</option>
                            </select>
                            <i class="fal fa-chevron-down ub-select-caret"></i>
                        </div>
                    </div>
                </div>

                <div class="ub-section-divider">
                    <span class="ub-section-label">{{ $t('companies.user.role') || 'Rôle' }}</span>
                </div>

                <div class="ub-field ub-field--full">
                    <div class="ub-roles">
                        <label
                            v-for="role in roles"
                            :key="role.value"
                            class="ub-role-card"
                            :class="{ 'is-selected': user.role === role.value }"
                        >
                            <input
                                type="radio"
                                class="ub-role-input"
                                :value="role.value"
                                v-model="user.role"
                            />
                            <div class="ub-role-header">
                                <span class="ub-role-radio" :class="{ 'is-checked': user.role === role.value }">
                                    <i v-if="user.role === role.value" class="fal fa-check"></i>
                                </span>
                                <span class="ub-role-name">{{ $t(role.labelKey) }}</span>
                            </div>
                            <p class="ub-role-desc">{{ $t(role.descKey) }}</p>
                        </label>
                    </div>
                    <p v-if="error.role" class="ub-error">{{ $t(`error.${error.role}`) }}</p>
                </div>

                <div class="ub-section-divider">
                    <span class="ub-section-label">{{ $t('companies.settings.sending_mails.password') }}</span>
                </div>

                <div class="ub-field ub-field--full">
                    <label class="ub-label" for="user-password">{{ $t('companies.settings.sending_mails.password') }}</label>
                    <input
                        id="user-password"
                        v-model="user.password"
                        type="password"
                        class="ub-input"
                        :placeholder="isEdit ? $t('placeholder.newPassword') : ''"
                    />
                    <p v-if="error.password" class="ub-error">{{ $t(`error.${error.password}`) }}</p>
                </div>

                <div v-if="!saved" class="ub-actions">
                    <router-link to="/company/users" class="ub-btn ub-btn--ghost">
                        {{ $t('common.cancel') || 'Annuler' }}
                    </router-link>
                    <button
                        type="submit"
                        class="ub-btn ub-btn--primary"
                        :disabled="loading"
                    >
                        <i v-if="loading" class="fas fa-circle-notch fa-spin"></i>
                        <i v-else class="fal fa-check"></i>
                        {{ submitButtonText }}
                    </button>
                </div>
            </form>
        </section>
    </main>
</template>

<script>
import { mapActions, mapState } from 'vuex'

export default {
    name: 'UserBuilder',
    data() {
        const userId = this.$route.params.userId
        const isEdit = !!userId
        return {
            loading: false,
            saved: false,
            isEdit,
            error: {},
            roles: [
                { value: 'admin', labelKey: 'companies.user.roles.admin', descKey: 'companies.user.roles.admin_desc' },
                { value: 'manager', labelKey: 'companies.user.roles.manager', descKey: 'companies.user.roles.manager_desc' },
                { value: 'agent', labelKey: 'companies.user.roles.agent', descKey: 'companies.user.roles.agent_desc' },
                { value: 'viewer', labelKey: 'companies.user.roles.viewer', descKey: 'companies.user.roles.viewer_desc' }
            ]
        }
    },
    computed: {
        ...mapState('company', {
            user: (state) => state.newUser
        }),
        title() {
            return this.isEdit
                ? this.$t('companies.user.edit')
                : this.$t('companies.user.create')
        },
        subtitle() {
            return this.isEdit
                ? this.$t('companies.user.edit_desc')
                : this.$t('companies.user.create_desc')
        },
        submitButtonText() {
            return this.isEdit
                ? this.$t('companies.user.edit_user')
                : this.$t('companies.user.create_user')
        }
    },
    created() {
        const userId = this.$route.params.userId
        if (userId) {
            this.loading = true
            this.editUser(userId)
                .catch((e) => {
                    this.$store.dispatch('dispatchError', e)
                })
                .finally(() => {
                    this.loading = false
                })
        }
    },
    methods: {
        ...mapActions('company', {
            createUser: 'createUser',
            updateUser: 'updateUser',
            editUser: 'editUser'
        }),
        async doCreate() {
            this.loading = true
            const userId = this.$route.params.userId
            const request = userId ? this.updateUser(this.user) : this.createUser(this.user)
            request
                .then(() => {
                    if (!userId) {
                        this.$store.commit('company/setNewUser', {
                            password: null,
                            name: null,
                            job_title: null,
                            email: null,
                            locale: null,
                            phone: null
                        })
                    }
                    this.error = {}
                    this.$store.dispatch('toast', {
                        type: 'success',
                        title: this.$t('success.title'),
                        message: this.$t('success.message')
                    })
                })
                .catch((e) => (this.error = e))
                .finally(() => {
                    this.loading = false
                })
        }
    },
    beforeDestroy() {
        this.$store.commit('company/setNewUser', {
            id: null,
            name: null,
            job_title: null,
            email: null,
            locale: null,
            role: null,
            phone: null
        })
    }
}
</script>

<style scoped>
.ub-v2 {
    width: 100%;
    max-width: 780px;
    margin: 0 auto;
    padding: 24px 32px 48px;
    font-family: var(--v2-font-sans);
    color: var(--v2-text-2);
}

.tbv2-page-title {
    font-size: 0.95rem;
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
    letter-spacing: -0.005em;
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.tbv2-crumb {
    color: var(--v2-text-3);
    text-decoration: none !important;
    font-weight: var(--v2-font-weight-medium);
    transition: color var(--v2-transition-fast);
}

.tbv2-crumb:hover {
    color: var(--v2-primary);
}

.tbv2-sep {
    color: var(--v2-text-4);
}

.tbv2-current {
    color: var(--v2-text-1);
}

.ub-hero {
    margin-bottom: var(--v2-space-5);
}

.ub-back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
    text-decoration: none !important;
    margin-bottom: 10px;
    transition: color var(--v2-transition-fast);
}

.ub-back:hover {
    color: var(--v2-primary);
}

.ub-back i {
    font-size: 0.75rem;
}

.ub-hero-title {
    margin: 0 0 6px;
    font-size: 1.75rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
    letter-spacing: -0.02em;
    line-height: 1.15;
}

.ub-hero-sub {
    margin: 0;
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-3);
    line-height: 1.5;
    max-width: 640px;
}

.ub-card {
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-lg);
    overflow: hidden;
}

.ub-card-header {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 18px 22px;
    border-bottom: 1px solid var(--v2-border);
}

.ub-card-icon {
    width: 40px;
    height: 40px;
    border-radius: var(--v2-radius-md);
    background: var(--v2-primary-soft);
    color: var(--v2-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    font-size: 1.05rem;
}

.ub-card-title {
    margin: 0;
    font-size: 1rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-1);
}

.ub-card-desc {
    margin: 2px 0 0;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
}

.ub-form {
    padding: 22px;
    display: flex;
    flex-direction: column;
    gap: 18px;
}

.ub-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 16px;
}

.ub-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
}

.ub-field--full {
    grid-column: 1 / -1;
}

.ub-label {
    font-size: var(--v2-text-xs-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-2);
    letter-spacing: 0.01em;
    margin: 0;
}

.ub-input {
    width: 100%;
    box-sizing: border-box;
    height: 38px;
    padding: 0 12px;
    background: var(--v2-surface-0);
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    font-size: var(--v2-text-sm-size);
    color: var(--v2-text-1);
    font-family: inherit;
    outline: none;
    transition: border-color var(--v2-transition-fast), box-shadow var(--v2-transition-fast);
}

.ub-input:hover {
    border-color: var(--v2-border-strong);
}

.ub-input:focus {
    border-color: var(--v2-primary);
    box-shadow: 0 0 0 3px rgba(8, 145, 178, 0.12);
}

.ub-input::placeholder {
    color: var(--v2-text-4);
}

.ub-select-wrapper {
    position: relative;
}

.ub-select {
    padding-right: 34px;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    cursor: pointer;
    background: var(--v2-surface-0);
}

.ub-select-caret {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 0.75rem;
    color: var(--v2-text-3);
    pointer-events: none;
}

.ub-section-divider {
    display: flex;
    align-items: center;
    padding-top: 6px;
    margin-top: 2px;
    border-top: 1px solid var(--v2-border-subtle);
}

.ub-section-label {
    font-size: 0.68rem;
    font-weight: var(--v2-font-weight-bold);
    color: var(--v2-text-3);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    padding-top: 14px;
}

/* Role cards */
.ub-roles {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 10px;
}

.ub-role-card {
    position: relative;
    padding: 12px 14px !important;
    border: 1px solid var(--v2-border);
    border-radius: var(--v2-radius-md);
    background: var(--v2-surface-0);
    cursor: pointer;
    transition: all var(--v2-transition-fast);
    display: block;
}

.ub-role-card:hover {
    border-color: var(--v2-border-strong);
}

.ub-role-card.is-selected {
    border-color: var(--v2-primary);
    background: var(--v2-primary-soft);
}

.ub-role-input {
    position: absolute;
    opacity: 0;
    pointer-events: none;
    width: 0;
    height: 0;
}

.ub-role-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 4px;
}

.ub-role-radio {
    width: 18px;
    height: 18px;
    border-radius: 50%;
    border: 2px solid var(--v2-border-strong);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: all var(--v2-transition-fast);
    background: var(--v2-surface-0);
}

.ub-role-radio i {
    color: #fff !important;
    font-size: 0.6rem;
}

.ub-role-radio.is-checked {
    background: var(--v2-primary);
    border-color: var(--v2-primary);
}

.ub-role-name {
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    color: var(--v2-text-1);
}

.ub-role-desc {
    font-size: var(--v2-text-xs-size);
    color: var(--v2-text-3);
    margin: 0;
    line-height: 1.4;
    padding-left: 26px;
}

.ub-error {
    margin: 0;
    font-size: var(--v2-text-xs-size);
    color: var(--v2-danger);
    font-weight: var(--v2-font-weight-medium);
}

/* Actions */
.ub-actions {
    display: flex;
    justify-content: flex-end;
    gap: 10px;
    padding-top: 14px;
    border-top: 1px solid var(--v2-border);
    margin-top: 4px;
}

.ub-btn {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    height: 36px;
    padding: 0 16px;
    border-radius: var(--v2-radius-md);
    font-size: var(--v2-text-sm-size);
    font-weight: var(--v2-font-weight-semibold);
    border: 1px solid transparent;
    cursor: pointer;
    text-decoration: none !important;
    transition: all var(--v2-transition-fast);
    font-family: inherit;
}

.ub-btn i {
    font-size: 0.8rem;
}

.ub-btn--ghost {
    background: transparent;
    border-color: var(--v2-border);
    color: var(--v2-text-2);
}

.ub-btn--ghost:hover {
    background: var(--v2-surface-2);
    color: var(--v2-text-1);
}

.ub-btn--primary {
    background: var(--v2-primary);
    color: #fff !important;
}

.ub-btn--primary i {
    color: #fff !important;
}

.ub-btn--primary:hover:not(:disabled) {
    background: var(--v2-primary-hover, #0e7490);
    box-shadow: var(--v2-shadow-sm);
}

.ub-btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}

@media (max-width: 900px) {
    .ub-v2 {
        padding: 16px 18px 32px;
    }

    .ub-hero-title {
        font-size: 1.375rem;
    }

    .ub-grid {
        grid-template-columns: 1fr;
    }

    .ub-form {
        padding: 16px;
    }

    .ub-card-header {
        padding: 14px 16px;
    }

    .ub-actions {
        flex-direction: column-reverse;
        align-items: stretch;
    }

    .ub-btn {
        justify-content: center;
    }
}
</style>
